import { z } from '@repo/schema'
import {
  type TableConfigurationJson,
  TableConfigurationJsonSchema,
} from '@repo/schema/table-configuration'

import { isAddedAfterRollout } from '../column-meta'
import { ROW_DENSITIES, type RowDensity } from '../row-density'
import type {
  ColumnField,
  ColumnSizingState,
  DisplayChoice,
  PersistedColumnPinningState,
} from '../tanstack-table'

/**
 * Field selection: the ids of the Column fields each column shows. Code fixes their order, so a
 * stored order means nothing.
 */
export type FieldSelection = Record<string, string[]>

const FieldSelectionSchema = z.record(z.string(), z.array(z.string()))

/**
 * The Items shown a List cell can choose, in order; the last is the default unless it sets one.
 * Comfortable rows keep each cell to two lines, so no more than two items.
 */
export const ITEMS_SHOWN_CHOICES = [1, 2] as const
export type ItemsShownCount = (typeof ITEMS_SHOWN_CHOICES)[number]
export const DEFAULT_ITEMS_SHOWN: ItemsShownCount = 2

/** The Items shown each List cell was set to; one missing shows its default. */
export type ItemsShown = Record<string, ItemsShownCount>

/** `itemsShown` with a List cell set to `count`; its `defaultCount` is stored as none. */
export const withItemsShown = (
  itemsShown: ItemsShown,
  columnId: string,
  count: ItemsShownCount,
  defaultCount: ItemsShownCount,
): ItemsShown => {
  const { [columnId]: _previous, ...others } = itemsShown
  return count === defaultCount ? others : { ...others, [columnId]: count }
}

/** The option each column's Display choices were set to, by column and choice id. */
export type DisplaySettings = Record<string, Record<string, string>>

/** Whether `value`, such as a stored one, is one of `choice`'s options. */
export const isDisplayOption = <TValue extends string>(
  choice: DisplayChoice<TValue>,
  value: TableConfigurationJson[string] | undefined,
): value is TValue => choice.options.some(option => option.value === value)

/** `display` with a column's Display choice set to `value`; the default is stored as none. */
export const withDisplayChoice = (
  display: DisplaySettings,
  columnId: string,
  choice: DisplayChoice,
  value: string,
): DisplaySettings => {
  const { [columnId]: current, ...others } = display
  const { [choice.id]: _previous, ...otherChoices } = current ?? {}
  const choices = value === choice.default ? otherChoices : { ...otherChoices, [choice.id]: value }
  return Object.keys(choices).length > 0 ? { ...others, [columnId]: choices } : others
}

/** The columns each per-column setting applies to. */
export type ColumnSettingDefs = {
  /** The Column fields (`meta.fields`) of each column with more than one. */
  columnFields: Record<string, readonly ColumnField[]>
  /** The Display choices (`meta.displayChoices`) of each column with any. */
  displayChoices: Record<string, readonly DisplayChoice[]>
  /** The List cells (`meta.listCell`), each with its default Items shown. */
  listCells: Record<string, ItemsShownCount>
}

/** The per-column settings a view saves with its columns. */
export type ColumnSettings = {
  /** Only the Display choices set to an option other than their default. */
  display: DisplaySettings
  /** A list for every column with Column fields, never empty. */
  fields: FieldSelection
  /** Only List cells set to other than their default. */
  itemsShown: ItemsShown
}

/** No column has a per-column setting, as outside a table. */
export const NO_COLUMN_SETTING_DEFS: ColumnSettingDefs = {
  columnFields: {},
  displayChoices: {},
  listCells: {},
}

/** Every per-column setting at its default. */
export const NO_COLUMN_SETTINGS: ColumnSettings = { display: {}, fields: {}, itemsShown: {} }

// An invalid count reads as none, so its column shows the default.
const ItemsShownSchema = z.record(z.string(), z.literal(ITEMS_SHOWN_CHOICES).nullable().catch(null))

// Any value per choice: the merge reads one that is no option as the default, and a save keeps
// those of choices this build does not know.
const StoredDisplaySchema = z.record(z.string(), TableConfigurationJsonSchema)

export interface MergedTableSettings {
  columnSettings: ColumnSettings
  order: string[]
  /** True when a stored order was discarded because it predates `columnOrderVersion`. */
  orderMigrated: boolean
  /**
   * Version this merge result should be persisted under: the stored version whenever it is
   * ahead of `columnOrderVersion`, otherwise `columnOrderVersion`. Never write a lower version
   * than was read, or a newer client's order would be downgraded by an older one.
   */
  orderVersion: number | undefined
  pinning: PersistedColumnPinningState
  sizing: ColumnSizingState
  visibility: Record<string, boolean>
}

export interface StoredTableSettings {
  /** Only a Table Configuration stores it; missing reads as Comfortable. */
  density?: RowDensity
  /** Sparse: only the Display choices a user set, as stored. */
  display?: Record<string, TableConfigurationJson>
  fields?: FieldSelection
  /** Sparse: only the List cells a user set. */
  itemsShown?: ItemsShown
  order?: string[]
  orderVersion?: number
  pinning?: PersistedColumnPinningState
  sizing?: ColumnSizingState
  /** Stored keys this build does not know, such as a newer build's; a save sends them back. */
  unknownKeys?: TableConfigurationJson
  visibility?: Record<string, boolean>
}

const STORED_KEYS = new Set<string>([
  'density',
  'display',
  'fields',
  'itemsShown',
  'order',
  'orderVersion',
  'pinning',
  'sizing',
  'visibility',
] satisfies (keyof StoredTableSettings)[])

interface MergeArgs {
  /**
   * Opt-in stamp for one-time forced column-order migrations. When set and the stored
   * settings carry a lower (or absent) version, the stored order is discarded in favour of
   * `initialColumnOrder`. Visibility, sizing and pinning are never affected. Tables that omit
   * this keep their stored order verbatim.
   */
  columnOrderVersion?: number
  columnSettingDefs?: ColumnSettingDefs
  initialColumnOrder: string[]
  initialColumnPinning: PersistedColumnPinningState
  initialColumnSizing: ColumnSizingState
  initialColumnVisibility: Record<string, boolean>
  /** The table's Known columns rollout, which Column fields' `addedOn` compare with. */
  knownColumnsRollout?: string
  lockedColumnPinning?: PersistedColumnPinningState
  /**
   * Columns added after the table's Known columns rollout. Stored settings show one only when
   * they store `true` for it; without stored settings it keeps its code default.
   */
  postRolloutColumnIds?: readonly string[]
  stored: StoredTableSettings | undefined
}

export const isOrderVersionStale = (
  storedVersion: number | undefined,
  columnOrderVersion: number | undefined,
): boolean => columnOrderVersion !== undefined && (storedVersion ?? 0) < columnOrderVersion

/**
 * The ids of `fields` that `ids` lists, and the fixed ones, in code order. A shown column always
 * keeps a field, so a list naming none of them, such as an empty one, shows the first.
 */
export const selectFields = (fields: readonly ColumnField[], ids: readonly string[]) => {
  const selected = fields
    .filter(field => field.fixed || ids.includes(field.id))
    .map(field => field.id)
  return selected.length > 0 ? selected : fields.slice(0, 1).map(field => field.id)
}

/**
 * The Display choices stored at an option of theirs other than the default; an invalid one reads
 * as none.
 */
const mergeDisplay = (
  displayChoices: Record<string, readonly DisplayChoice[]>,
  stored: StoredTableSettings['display'],
): DisplaySettings =>
  Object.fromEntries(
    Object.entries(displayChoices).flatMap(([columnId, choices]) => {
      const set = choices.flatMap(choice => {
        const value = stored?.[columnId]?.[choice.id]
        return isDisplayOption(choice, value) && value !== choice.default
          ? [[choice.id, value] as const]
          : []
      })
      return set.length > 0 ? [[columnId, Object.fromEntries(set)] as const] : []
    }),
  )

/**
 * Every column's Field selection: the fields stored settings list for it, else every field that
 * existed at the Known columns rollout, as in a Default never saved.
 */
const mergeFields = (
  columnFields: Record<string, readonly ColumnField[]>,
  stored: StoredTableSettings | undefined,
  knownColumnsRollout: string | undefined,
): FieldSelection =>
  Object.fromEntries(
    Object.entries(columnFields).map(([columnId, fields]) => {
      const ids =
        stored?.fields?.[columnId] ??
        fields
          .filter(field => !isAddedAfterRollout(field.addedOn, knownColumnsRollout))
          .map(field => field.id)
      return [columnId, selectFields(fields, ids)]
    }),
  )

export const mergeTableSettings = ({
  columnOrderVersion,
  columnSettingDefs = NO_COLUMN_SETTING_DEFS,
  initialColumnOrder,
  initialColumnPinning,
  initialColumnSizing,
  initialColumnVisibility,
  knownColumnsRollout,
  lockedColumnPinning,
  postRolloutColumnIds = [],
  stored,
}: MergeArgs): MergedTableSettings => {
  const validIds = new Set(initialColumnOrder)
  const isValid = (id: string) => validIds.has(id)

  const shouldMigrateOrder = isOrderVersionStale(stored?.orderVersion, columnOrderVersion)

  // A stored version ahead of this client's means a newer deploy wrote it. Keep its ids even
  // though they are unknown here, and keep its version, so this client cannot downgrade the
  // order it does not fully understand.
  const isStoredOrderAhead =
    stored?.orderVersion !== undefined &&
    (columnOrderVersion === undefined || stored.orderVersion > columnOrderVersion)

  const filteredStoredOrder = shouldMigrateOrder
    ? []
    : (stored?.order ?? []).filter(id => isStoredOrderAhead || validIds.has(id))
  const order = mergeOrder(filteredStoredOrder, initialColumnOrder)
  const orderMigrated = shouldMigrateOrder && (stored?.order?.length ?? 0) > 0
  const orderVersion = isStoredOrderAhead ? stored.orderVersion : columnOrderVersion

  const filteredStoredVisibility = filterRecord(stored?.visibility, isValid)
  const visibility = { ...initialColumnVisibility, ...filteredStoredVisibility }
  if (stored) {
    for (const id of postRolloutColumnIds) visibility[id] = filteredStoredVisibility[id] === true
  }

  const sizing = stored?.sizing
    ? { ...filterRecord(initialColumnSizing, isValid), ...filterRecord(stored.sizing, isValid) }
    : filterRecord(initialColumnSizing, isValid)

  const pinning = mergePinning(initialColumnPinning, stored?.pinning, lockedColumnPinning, validIds)

  const columnSettings = {
    display: mergeDisplay(columnSettingDefs.displayChoices, stored?.display),
    fields: mergeFields(columnSettingDefs.columnFields, stored, knownColumnsRollout),
    // A stored default reads as none, so it compares equal to a view that never set one.
    itemsShown: filterRecord(
      stored?.itemsShown,
      id =>
        id in columnSettingDefs.listCells &&
        stored?.itemsShown?.[id] !== columnSettingDefs.listCells[id],
    ),
  }

  return { columnSettings, order, orderMigrated, orderVersion, pinning, sizing, visibility }
}

export const normalizeBackendTableSettings = (
  backendTable: unknown,
): StoredTableSettings | undefined => {
  if (!backendTable || typeof backendTable !== 'object' || Array.isArray(backendTable)) {
    return undefined
  }
  const table = backendTable as {
    density?: unknown
    display?: unknown
    fields?: unknown
    itemsShown?: unknown
    order?: unknown
    orderVersion?: unknown
    pinning?: unknown
    sizing?: unknown
    visibility?: unknown
  }

  const order = Array.isArray(table.order)
    ? table.order.filter((o): o is string => typeof o === 'string')
    : undefined

  const orderVersion = typeof table.orderVersion === 'number' ? table.orderVersion : undefined

  const pinning = normalizePinning(table.pinning)

  const sizing =
    table.sizing && typeof table.sizing === 'object' && !Array.isArray(table.sizing)
      ? (table.sizing as Record<string, number>)
      : undefined

  const visibility =
    table.visibility && typeof table.visibility === 'object' && !Array.isArray(table.visibility)
      ? (table.visibility as Record<string, boolean>)
      : undefined

  const density = ROW_DENSITIES.find(({ value }) => value === table.density)?.value

  const display = StoredDisplaySchema.safeParse(table.display).data

  const fields = FieldSelectionSchema.safeParse(table.fields).data

  const itemsShown = Object.entries(ItemsShownSchema.safeParse(table.itemsShown).data ?? {}).filter(
    (entry): entry is [string, ItemsShownCount] => entry[1] !== null,
  )

  const unknownKeys = Object.entries(
    TableConfigurationJsonSchema.safeParse(backendTable).data ?? {},
  ).filter(([key]) => !STORED_KEYS.has(key))

  return {
    density,
    display,
    fields,
    itemsShown: itemsShown.length > 0 ? Object.fromEntries(itemsShown) : undefined,
    order,
    orderVersion,
    pinning,
    sizing,
    unknownKeys: unknownKeys.length > 0 ? Object.fromEntries(unknownKeys) : undefined,
    visibility,
  }
}

/**
 * Stored per-column settings of columns without that setting here, which a newer build may have
 * saved, under the ones the table shows; none when both are empty.
 */
const keepOtherColumns = <T>(
  stored: Record<string, T> | undefined,
  shown: Record<string, T> | undefined,
  isHere: (_id: string) => boolean,
) => {
  const kept = { ...filterRecord(stored, id => !isHere(id)), ...shown }
  return Object.keys(kept).length > 0 ? kept : undefined
}

/** Stored Display choices a column does not have here, of any column, under the ones shown. */
const keepOtherChoices = (
  stored: StoredTableSettings['display'],
  shown: StoredTableSettings['display'],
  displayChoices: ColumnSettingDefs['displayChoices'],
) => {
  const columnIds = new Set([...Object.keys(stored ?? {}), ...Object.keys(shown ?? {})])
  const kept = [...columnIds].flatMap(columnId => {
    const choices = keepOtherColumns(stored?.[columnId], shown?.[columnId], choiceId =>
      Boolean(displayChoices[columnId]?.some(choice => choice.id === choiceId)),
    )
    return choices ? [[columnId, choices] as const] : []
  })
  return kept.length > 0 ? Object.fromEntries(kept) : undefined
}

/**
 * Adds back what `stored` holds that the table cannot show: keys this build does not know,
 * entries for columns the table does not have, and per-column settings of columns without that
 * setting here. A newer build may have saved them, so they stay.
 */
export const keepUnknownSettings = (
  columns: StoredTableSettings,
  stored: StoredTableSettings | undefined,
  tableColumnIds: ReadonlySet<string>,
  { columnFields, displayChoices, listCells }: ColumnSettingDefs,
): StoredTableSettings => {
  const isUnknown = (id: string) => !tableColumnIds.has(id)
  return {
    ...columns,
    display: keepOtherChoices(stored?.display, columns.display, displayChoices),
    fields: keepOtherColumns(stored?.fields, columns.fields, id => id in columnFields),
    itemsShown: keepOtherColumns(stored?.itemsShown, columns.itemsShown, id => id in listCells),
    sizing: { ...filterRecord(stored?.sizing, isUnknown), ...columns.sizing },
    unknownKeys: stored?.unknownKeys,
    visibility: { ...filterRecord(stored?.visibility, isUnknown), ...columns.visibility },
  }
}

const mergeOrder = (storedOrder: string[], defaultOrder: string[]): string[] => {
  if (storedOrder.length === 0) return [...defaultOrder]

  const result = uniqueIds(storedOrder)
  const present = new Set(result)

  for (let i = 0; i < defaultOrder.length; i++) {
    const id = defaultOrder[i]
    if (present.has(id)) continue

    let insertAt = 0
    for (let j = i - 1; j >= 0; j--) {
      const prevId = defaultOrder[j]
      const idx = result.indexOf(prevId)
      if (idx !== -1) {
        insertAt = idx + 1
        break
      }
    }
    result.splice(insertAt, 0, id)
    present.add(id)
  }

  return result
}

const filterRecord = <T>(
  record: Record<string, T> | undefined,
  keepId: (_id: string) => boolean,
): Record<string, T> => {
  if (!record) return {}
  const out: Record<string, T> = {}
  for (const [key, value] of Object.entries(record)) {
    if (keepId(key)) out[key] = value
  }
  return out
}

const normalizePinning = (value: unknown): PersistedColumnPinningState | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined

  return {
    left: normalizePinningIds('left' in value ? value.left : undefined),
    right: normalizePinningIds('right' in value ? value.right : undefined),
  }
}

const normalizePinningIds = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((id): id is string => typeof id === 'string') : []

const mergePinning = (
  initial: PersistedColumnPinningState,
  stored: PersistedColumnPinningState | undefined,
  locked: PersistedColumnPinningState | undefined,
  validIds: Set<string>,
): PersistedColumnPinningState => {
  const collectUnique = (ids: string[], seen: Set<string>) => {
    const result: string[] = []
    for (const id of ids) {
      if (validIds.has(id) && !seen.has(id)) {
        result.push(id)
        seen.add(id)
      }
    }
    return result
  }

  const lockedSeen = new Set<string>()
  const lockedLeft = collectUnique(locked?.left ?? [], lockedSeen)
  const lockedRight = keepActionsLast(collectUnique(locked?.right ?? [], lockedSeen))
  const sourceSeen = new Set<string>()
  const source = stored ?? initial
  const left = collectUnique(source.left ?? [], sourceSeen)
  const right = collectUnique(source.right ?? [], sourceSeen)
  const lockedIds = new Set([...lockedLeft, ...lockedRight])

  return {
    left: [...lockedLeft, ...left.filter(id => !lockedIds.has(id))],
    right: [...right.filter(id => !lockedIds.has(id)), ...lockedRight],
  }
}

const uniqueIds = (ids: string[]): string[] => {
  const seen = new Set<string>()
  return ids.filter(id => {
    if (seen.has(id)) return false
    seen.add(id)
    return true
  })
}

const keepActionsLast = (ids: string[]): string[] =>
  ids.includes('actions') ? [...ids.filter(id => id !== 'actions'), 'actions'] : ids
